import {
  HttpClient,
  HttpEvent,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Observable } from 'rxjs';
import { ScenarioDto } from '../model/scenarioDto';
import { ClearingDataFormArrayDto } from '../model/clearingDataFormArrayDto';
import { ClearingDataAttribute } from '../model/clearingDataAttribute';
import { Configuration } from '../configuration';
import {BASE_PATH, LIGHT_PARTNER_API_PATH} from '../variables';
import { TargetStateDto } from '../model/targetStateDto';
import { PlatformPartner } from '../model/platformPartner';
import { RoutingConfig } from '../model/routingConfig';
import { SlaPartnerResolutionTime } from '../model/slaPartnerResolutionTime';
import { RoutingConfigTypeEnum } from '../model/routingConfigTypeEnum';
import { Notification } from '../model/notification';
import { UserEligibilityDto } from '../model/userEligibilityDto';
import { SeverityType } from '../../clearing';
import { StatusType } from '../../clearing';
import { CustomHttpUrlEncodingCodec } from '../../clearing/encoder';
import { ClearingTicketChange } from '../model/clearingTicketChange';
import { TicketSlaResolutionTimeStatus } from '../model/ticketSlaResolutionTimeStatus';
import { ClearingTicketTemplate } from '../model/clearingTicketTemplate';
import { ClearingTicketBase, SyncChangeDto } from '../model/clearingTicketBase';
import { TicketCategoryEnum } from '../model/ticketCategoryEnum';
import { SubScenarioDto } from '../model/subScenarioDto';
import { RequestCancellationCreateDto } from '../model/requestCancellationCreateDto';
import { CustomFiltersEnum } from '../model/customFiltersEnum';
import { OwnerScopeDto } from '../model/ownerScopeDto';
import { CreateDynamicAccessRole } from '../model/createDynamicAccessRole';
import { DynamicAccessRole } from '../model/dynamicAccessRole';
import { InternalNoteCreate } from '../model/internalNoteCreate';
import {ITU_CODE, LightPartnerService} from '@adlatus-gui/domain/clearing-custom';
import {isLightRoute} from "@adlatus-gui/shared";

@Injectable()
export class ClearingTicketCustomService {
  public baseConfigPath = '';
  public defaultHeaders = new HttpHeaders();
  public configuration = new Configuration();

  constructor(
    protected httpClient: HttpClient,
    @Optional() @Inject(BASE_PATH) baseConfigPath: string,
    @Optional() configuration: Configuration,
    @Inject(ITU_CODE) private ituCode: string[],
    @Inject(LIGHT_PARTNER_API_PATH) private lightPartnerApiPath: string

  ) {
    if (baseConfigPath) {
      this.baseConfigPath = baseConfigPath;
    }
    if (configuration) {
      this.configuration = configuration;
      this.baseConfigPath =
        baseConfigPath || configuration.basePath || this.baseConfigPath;
    }
    if (isLightRoute()) {
      this.baseConfigPath = this.baseConfigPath + this.lightPartnerApiPath;
    }
  }

  /**
   * List Scenarios
   * This operation lists all Scenarios with corresponding subscenarios.
   * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
   * @param reportProgress flag to report request and response progress.
   */
  public listScenarios(
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<Array<ScenarioDto>>;
  public listScenarios(
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<Array<ScenarioDto>>>;
  public listScenarios(
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<Array<ScenarioDto>>>;
  public listScenarios(
    observe: any = 'body',
    reportProgress = false
  ): Observable<any> {
    let headers = this.defaultHeaders;

    // authentication (basicAuth) required
    if (this.configuration.username || this.configuration.password) {
      headers = headers.set(
        'Authorization',
        'Basic ' +
          btoa(this.configuration.username + ':' + this.configuration.password)
      );
    }

    // authentication (bearer) required
    if (this.configuration.accessToken) {
      const accessToken =
        typeof this.configuration.accessToken === 'function'
          ? this.configuration.accessToken()
          : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }
    // to determine the Accept header
    const httpHeaderAccepts: string[] = ['application/json;charset=utf-8'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = [];
    return this.httpClient.request<Array<ScenarioDto>>(
      'get',
      `${this.baseConfigPath}/troubleTicket/scenarios`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress,
      }
    );
  }

  public retrieveScenarioById(
    id:string,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<SubScenarioDto>;
  public retrieveScenarioById(
    id:string,

    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<SubScenarioDto>>;
  public retrieveScenarioById(
    id:string,

    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<SubScenarioDto>>;
  public retrieveScenarioById(
    id:string,

    observe: any = 'body',
    reportProgress = false
  ): Observable<any> {
    let headers = this.defaultHeaders;

    // authentication (basicAuth) required
    if (this.configuration.username || this.configuration.password) {
      headers = headers.set(
        'Authorization',
        'Basic ' +
        btoa(this.configuration.username + ':' + this.configuration.password)
      );
    }

    // authentication (bearer) required
    if (this.configuration.accessToken) {
      const accessToken =
        typeof this.configuration.accessToken === 'function'
          ? this.configuration.accessToken()
          : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }
    // to determine the Accept header
    const httpHeaderAccepts: string[] = ['application/json;charset=utf-8'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = [];
    return this.httpClient.request<Array<ScenarioDto>>(
      'get',
      `${this.baseConfigPath}/troubleTicket/scenarios/${id}`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress,
      }
    );
  }


  public listPlatformOwnerSupportedScenarios(
    ownerItuCode: string,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<Array<SubScenarioDto>>;
  public listPlatformOwnerSupportedScenarios(
    ownerItuCode: string,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<Array<SubScenarioDto>>>;
  public listPlatformOwnerSupportedScenarios(
    ownerItuCode: string,
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<Array<SubScenarioDto>>>;
  public listPlatformOwnerSupportedScenarios(
    ownerItuCode: string,
    observe: any = 'body',
    reportProgress = false
  ): Observable<any> {
    let headers = this.defaultHeaders;
    let queryParameters = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    queryParameters = queryParameters.set('owner', ownerItuCode);

    // authentication (basicAuth) required
    if (this.configuration.username || this.configuration.password) {
      headers = headers.set(
        'Authorization',
        'Basic ' +
        btoa(this.configuration.username + ':' + this.configuration.password)
      );
    }

    // authentication (bearer) required
    if (this.configuration.accessToken) {
      const accessToken =
        typeof this.configuration.accessToken === 'function'
          ? this.configuration.accessToken()
          : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }
    // to determine the Accept header
    const httpHeaderAccepts: string[] = ['application/json;charset=utf-8'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = [];
    return this.httpClient.request<Array<SubScenarioDto>>(
      'get',
      `${this.baseConfigPath}/troubleTicket/scenarios/supported`,
      {
        params: queryParameters,
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress,
      }
    );
  }


  public setPlatformOwnerSupportedScenarios(
    ownerItuCode: string,
    selectedScenarios: SubScenarioDto[],
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<VoidFunction>;
  public setPlatformOwnerSupportedScenarios(
    ownerItuCode: string,
    selectedScenarios: SubScenarioDto[],
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<VoidFunction>;
  public setPlatformOwnerSupportedScenarios(
    ownerItuCode: string,
    selectedScenarios: SubScenarioDto[],
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<VoidFunction>;
  public setPlatformOwnerSupportedScenarios(
    ownerItuCode: string,
    selectedScenarios: SubScenarioDto[],
    observe: any = 'body',
    reportProgress = false
  ): Observable<any> {
    let headers = this.defaultHeaders;
    let queryParameters = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    queryParameters = queryParameters.set('owner', ownerItuCode);

    // authentication (basicAuth) required
    if (this.configuration.username || this.configuration.password) {
      headers = headers.set(
        'Authorization',
        'Basic ' +
        btoa(this.configuration.username + ':' + this.configuration.password)
      );
    }

    // authentication (bearer) required
    if (this.configuration.accessToken) {
      const accessToken =
        typeof this.configuration.accessToken === 'function'
          ? this.configuration.accessToken()
          : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }
    // to determine the Accept header
    const httpHeaderAccepts: string[] = ['application/json;charset=utf-8'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = [];
    return this.httpClient.request<Array<SubScenarioDto>>(
      'post',
      `${this.baseConfigPath}/troubleTicket/scenarios/supported`,
      {
        params: queryParameters,
        body: selectedScenarios,
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress,
      }
    );
  }



  public listClearingTicketRequiredFields(
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<Array<string>>;
  public listClearingTicketRequiredFields(
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<Array<string>>>;
  public listClearingTicketRequiredFields(
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<Array<string>>>;
  public listClearingTicketRequiredFields(
    observe: any = 'body',
    reportProgress = false
  ): Observable<any> {
    let headers = this.defaultHeaders;

    // authentication (basicAuth) required
    if (this.configuration.username || this.configuration.password) {
      headers = headers.set(
        'Authorization',
        'Basic ' +
          btoa(this.configuration.username + ':' + this.configuration.password)
      );
    }

    // authentication (bearer) required
    if (this.configuration.accessToken) {
      const accessToken =
        typeof this.configuration.accessToken === 'function'
          ? this.configuration.accessToken()
          : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }
    // to determine the Accept header
    const httpHeaderAccepts: string[] = ['application/json;charset=utf-8'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = [];
    return this.httpClient.request<Array<ScenarioDto>>(
      'get',
      `${this.baseConfigPath}/troubleTicket/required-fields`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress,
      }
    );
  }

  public retrieveClearingTicketHistory(
    id: string,
    observe: any = 'body',
    reportProgress = false
  ): Observable<any> {
    if (id === null || id === undefined) {
      throw new Error(
        'Required parameter id was null or undefined when calling retrieveClearingTicketHistory.'
      );
    }

    let headers = this.defaultHeaders;

    if (this.configuration.username || this.configuration.password) {
      headers = headers.set(
        'Authorization',
        'Basic ' +
          btoa(this.configuration.username + ':' + this.configuration.password)
      );
    }

    if (this.configuration.accessToken) {
      const accessToken =
        typeof this.configuration.accessToken === 'function'
          ? this.configuration.accessToken()
          : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    const httpHeaderAccepts: string[] = ['application/json;charset=utf-8'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    const consumes: string[] = [];

    return this.httpClient.request<ClearingTicketChange[]>(
      'get',
      `${this.baseConfigPath}/troubleTicket/${encodeURIComponent(
        String(id)
      )}/history`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress,
      }
    );
  }

  public addInternalNote(
    id: string,
    body: InternalNoteCreate,
    observe: any = 'body',
    reportProgress = false
  ): Observable<any> {
    if (id === null || id === undefined) {
      throw new Error(
        'Required parameter id was null or undefined when calling addInternalNote.'
      );
    }
    if (body === null || body === undefined) {
      throw new Error(
        'Required parameter body was null or undefined when calling addInternalNote.'
      );
    }

    let headers = this.defaultHeaders;

    if (this.configuration.username || this.configuration.password) {
      headers = headers.set(
        'Authorization',
        'Basic ' +
          btoa(this.configuration.username + ':' + this.configuration.password)
      );
    }

    if (this.configuration.accessToken) {
      const accessToken =
        typeof this.configuration.accessToken === 'function'
          ? this.configuration.accessToken()
          : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    const httpHeaderAccepts: string[] = ['application/json;charset=utf-8'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    const consumes: string[] = ['application/json;charset=utf-8'];
    const httpContentTypeSelected: string | undefined =
      this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected != undefined) {
      headers = headers.set('Content-Type', httpContentTypeSelected);
    }

    return this.httpClient.request<void>(
      'post',
      `${this.baseConfigPath}/troubleTicket/${encodeURIComponent(
        String(id)
      )}/internal-note`,
      {
        body,
        withCredentials: this.configuration.withCredentials,
        headers,
        observe,
        reportProgress,
      }
    );
  }

  /**
   * List Create Ticket Scenarios
   * This operation lists all Scenarios with corresponding subscenarios for create ticket form.
   * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
   * @param reportProgress flag to report request and response progress.
   */

  public listCreateTicketScenarios(
    ownerItuCode: string,
    partnerItuCode: string,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<Array<ScenarioDto>>;
  public listCreateTicketScenarios(
    ownerItuCode: string,
    partnerItuCode: string,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<Array<ScenarioDto>>>;
  public listCreateTicketScenarios(
    ownerItuCode: string,
    partnerItuCode: string,
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<Array<ScenarioDto>>>;
  public listCreateTicketScenarios(
    ownerItuCode: string,
    partnerItuCode: string,
    observe: any = 'body',
    reportProgress = false
  ): Observable<any> {
    let headers = this.defaultHeaders;
    let queryParameters = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });
    if (partnerItuCode !== undefined && partnerItuCode !== null && ownerItuCode !== undefined && ownerItuCode !== null) {
      queryParameters = new HttpParams()
        .set('ownerItuCode', ownerItuCode as string)
        .set('partnerItuCode', partnerItuCode as string);
    }

    // authentication (basicAuth) required
    if (this.configuration.username || this.configuration.password) {
      headers = headers.set(
        'Authorization',
        'Basic ' +
          btoa(this.configuration.username + ':' + this.configuration.password)
      );
    }

    // authentication (bearer) required
    if (this.configuration.accessToken) {
      const accessToken =
        typeof this.configuration.accessToken === 'function'
          ? this.configuration.accessToken()
          : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }
    // to determine the Accept header
    const httpHeaderAccepts: string[] = ['application/json;charset=utf-8'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = [];
    return this.httpClient.request<Array<ScenarioDto>>(
      'get',
      `${this.baseConfigPath}/troubleTicket/scenarios/authorized-mutual-supported`,
      {
        params: queryParameters,
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress,
      }
    );
  }


  public listCreateSlaScenarios(
    ownerItuCode: string,
    partnerItuCode: string,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<Array<ScenarioDto>>;
  public listCreateSlaScenarios(
    ownerItuCode: string,
    partnerItuCode: string,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<Array<ScenarioDto>>>;
  public listCreateSlaScenarios(
    ownerItuCode: string,
    partnerItuCode: string,
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<Array<ScenarioDto>>>;
  public listCreateSlaScenarios(
    ownerItuCode: string,
    partnerItuCode: string,
    observe: any = 'body',
    reportProgress = false
  ): Observable<any> {
    let headers = this.defaultHeaders;
    let queryParameters = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });
    if (ownerItuCode !== undefined && ownerItuCode !== null) {
      queryParameters = queryParameters.set(
        'ownerItuCode',
        <string>ownerItuCode
      );
    }
    if (partnerItuCode !== undefined && partnerItuCode !== null) {
      queryParameters = queryParameters.set(
        'partnerItuCode',
        <string>partnerItuCode
      );
    }

    // authentication (basicAuth) required
    if (this.configuration.username || this.configuration.password) {
      headers = headers.set(
        'Authorization',
        'Basic ' +
        btoa(this.configuration.username + ':' + this.configuration.password)
      );
    }

    // authentication (bearer) required
    if (this.configuration.accessToken) {
      const accessToken =
        typeof this.configuration.accessToken === 'function'
          ? this.configuration.accessToken()
          : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }
    // to determine the Accept header
    const httpHeaderAccepts: string[] = ['application/json;charset=utf-8'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = [];
    return this.httpClient.request<Array<ScenarioDto>>(
      'get',
      `${this.baseConfigPath}/troubleTicket/scenarios/mutual-supported`,
      {
        params: queryParameters,
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress,
      }
    );
  }

  /**
   * Retrieves number of clearing tickets
   * This operation retrieves number of ClearingTicket entities
   * @param externalId Select only clearing tickets with externalId equal &#x27;externalId&#x27;. An empty parameter is ignored.
   * @param ticketType Select only clearing tickets with scenarios &#x3D; &#x27;ticketType&#x27; (allows pattern: e.g. 1.* selects all scenarios 1.0 — 1.9). An empty parameter is ignored.
   * @param status Select only clearing tickets with status equal &#x27;status&#x27;. An empty parameter is ignored.
   * @param originator Select only clearing tickets with originator equal &#x27;originator&#x27;. An empty parameter is ignored.
   * @param processor Select only clearing tickets with processor equal &#x27;processor&#x27;. An empty parameter is ignored.
   * @param severity Select only clearing tickets with severity equal &#x27;severity&#x27;. An empty parameter is ignored.
   * @param requestedResolutionDateFrom Select only clearing tickets with requestedResolutionDate higher &#x27;requestedResolutionDate&#x27;. An empty parameter is ignored.
   * @param requestedResolutionDateTo Select only clearing tickets with requestedResolutionDate less equal &#x27;requestedResolutionDate&#x27;. An empty parameter is ignored.
   * @param creationDateFrom Select only clearing tickets with creation date equal or higher &#x27;creationDateFrom&#x27; An empty parameter is ignored.
   * @param creationDateTo Select only clearing tickets with creation date less equal &#x27;creationDateTo&#x27; An empty parameter is ignored.
   * @param lastUpdateFrom Select only clearing tickets with last update equal or higher &#x27;lastUpdateFrom&#x27;. An empty parameter is ignored.
   * @param lastUpdateTo Select only clearing tickets with last update less equal &#x27;lastUpdateTo&#x27;. An empty parameter is ignored.
   * @param offset Requested index for start of selected clearing tickets to be provided in response. Note: Sort order is always by creation date.
   * @param limit Requested number of selected clearing tickets to be provided in response. Note: Sort order is always by creation date.
   * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
   * @param reportProgress flag to report request and response progress.
   */
  public countClearingTicket(
    externalId?: string,
    ticketType?: string,
    currentStatus?: string,
    originator?: string,
    processor?: string,
    severity?: SeverityType,
    requestedResolutionDateFrom?: string,
    requestedResolutionDateTo?: string,
    creationDateFrom?: string,
    creationDateTo?: string,
    lastUpdateFrom?: string,
    lastUpdateTo?: Date,
    lastStatusChangeFrom?: string,
    myWatchingIssues?: boolean,
    myIssues?: boolean,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<number>;
  public countClearingTicket(
    externalId?: string,
    ticketType?: string,
    currentStatus?: string,
    originator?: string,
    processor?: string,
    severity?: SeverityType,
    requestedResolutionDateFrom?: string,
    requestedResolutionDateTo?: string,
    creationDateFrom?: string,
    creationDateTo?: string,
    lastUpdateFrom?: string,
    lastUpdateTo?: Date,
    lastStatusChangeFrom?: string,
    myWatchingIssues?: boolean,
    myIssues?: boolean,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<number>>;
  public countClearingTicket(
    externalId?: string,
    ticketType?: string,
    currentStatus?: string,
    originator?: string,
    processor?: string,
    severity?: SeverityType,
    requestedResolutionDateFrom?: string,
    requestedResolutionDateTo?: string,
    creationDateFrom?: string,
    creationDateTo?: string,
    lastUpdateFrom?: string,
    lastUpdateTo?: Date,
    lastStatusChangeFrom?: string,
    myWatchingIssues?: boolean,
    myIssues?: boolean,
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<number>>;
  public countClearingTicket(
    externalId?: string,
    ticketType?: string,
    currentStatus?: string,
    originator?: string,
    processor?: string,
    severity?: SeverityType,
    requestedResolutionDateFrom?: string,
    requestedResolutionDateTo?: string,
    creationDateFrom?: string,
    creationDateTo?: string,
    lastUpdateFrom?: string,
    lastUpdateTo?: Date,
    lastStatusChangeFrom?: string,
    myWatchingIssues?: boolean,
    myIssues?: boolean,
    observe: any = 'body',
    reportProgress: boolean = false
  ): Observable<any> {
    let queryParameters = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });
    if (externalId !== undefined && externalId !== null) {
      queryParameters = queryParameters.set('externalId', <any>externalId);
    }
    if (ticketType !== undefined && ticketType !== null) {
      queryParameters = queryParameters.set('ticketType', <any>ticketType);
    }
    if (currentStatus !== undefined && currentStatus !== null) {
      queryParameters = queryParameters.set('currentStatus', currentStatus);
    }
    if (originator !== undefined && originator !== null) {
      queryParameters = queryParameters.set('originator', <any>originator);
    }
    if (processor !== undefined && processor !== null) {
      queryParameters = queryParameters.set('processor', <any>processor);
    }
    if (severity !== undefined && severity !== null) {
      queryParameters = queryParameters.set('severity', <any>severity);
    }
    if (
      requestedResolutionDateFrom !== undefined &&
      requestedResolutionDateFrom !== null
    ) {
      queryParameters = queryParameters.set(
        'requestedResolutionDateFrom',
        <any>requestedResolutionDateFrom
      );
    }
    if (
      requestedResolutionDateTo !== undefined &&
      requestedResolutionDateTo !== null
    ) {
      queryParameters = queryParameters.set(
        'requestedResolutionDateTo',
        <any>requestedResolutionDateTo
      );
    }
    if (creationDateFrom !== undefined && creationDateFrom !== null) {
      queryParameters = queryParameters.set(
        'creationDateFrom',
        <any>creationDateFrom
      );
    }
    if (creationDateTo !== undefined && creationDateTo !== null) {
      queryParameters = queryParameters.set(
        'creationDateTo',
        <any>creationDateTo
      );
    }
    if (lastUpdateFrom !== undefined && lastUpdateFrom !== null) {
      queryParameters = queryParameters.set(
        'lastUpdateFrom',
        <any>lastUpdateFrom
      );
    }
    if (lastUpdateTo !== undefined && lastUpdateTo !== null) {
      queryParameters = queryParameters.set(
        'lastUpdateTo',
        <any>lastUpdateTo.toISOString()
      );
    }
    if (lastStatusChangeFrom !== undefined && lastStatusChangeFrom !== null) {
      queryParameters = queryParameters.set(
        'lastStatusChangeFrom',
        <any>lastStatusChangeFrom
      );
    }

    let headers = this.defaultHeaders;

    // authentication (basicAuth) required
    if (this.configuration.username || this.configuration.password) {
      headers = headers.set(
        'Authorization',
        'Basic ' +
          btoa(this.configuration.username + ':' + this.configuration.password)
      );
    }

    // authentication (bearer) required
    if (this.configuration.accessToken) {
      const accessToken =
        typeof this.configuration.accessToken === 'function'
          ? this.configuration.accessToken()
          : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }
    // to determine the Accept header
    const httpHeaderAccepts: string[] = ['application/json;charset=utf-8'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    if (myWatchingIssues) {
      headers = headers.append('Custom-Filter', 'my_watching_issues');
    }

    if (myIssues) {
      headers = headers.append('Custom-Filter', 'my_issues');
    }



    // to determine the Content-Type header
    const consumes: string[] = [];

    return this.httpClient.request<number>(
      'get',
      `${this.baseConfigPath}/troubleTicket/count`,
      {
        params: queryParameters,
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress,
      }
    );
  }

  public getTotalClearingTicketCount(
    inbox: boolean |undefined,
    outbox: boolean |undefined,
  ): Observable<number>;
  public getTotalClearingTicketCount(
    inbox: boolean |undefined,
    outbox: boolean |undefined,
  ): Observable<HttpResponse<number>>;
  public getTotalClearingTicketCount(
    inbox: boolean |undefined,
    outbox: boolean |undefined,
  ): Observable<HttpEvent<number>>;
  public getTotalClearingTicketCount(
    inbox: boolean |undefined,
    outbox: boolean |undefined,
  ): Observable<any> {
    let queryParameters = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });


    let headers = this.defaultHeaders;

    if (inbox) {
      headers = headers.append('Custom-Filter', CustomFiltersEnum.INBOX);
    }
    if (outbox) {
      headers = headers.append('Custom-Filter', CustomFiltersEnum.OUTBOX);
    }

    // authentication (basicAuth) required
    if (this.configuration.username || this.configuration.password) {
      headers = headers.set(
        'Authorization',
        'Basic ' +
          btoa(this.configuration.username + ':' + this.configuration.password)
      );
    }

    // authentication (bearer) required
    if (this.configuration.accessToken) {
      const accessToken =
        typeof this.configuration.accessToken === 'function'
          ? this.configuration.accessToken()
          : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }
    // to determine the Accept header
    const httpHeaderAccepts: string[] = ['application/json;charset=utf-8'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = [];

    return this.httpClient.request<number>(
      'get',
      `${this.baseConfigPath}/troubleTicket/count/total`,
      {
        params: queryParameters,
        withCredentials: this.configuration.withCredentials,
        headers: headers,
      }
    );
  }

  public retrieveDashboardStatistics(): Observable<any> {
    const queryParameters = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });

    let headers = this.defaultHeaders;

    if (this.configuration.username || this.configuration.password) {
      headers = headers.set(
        'Authorization',
        'Basic ' +
          btoa(this.configuration.username + ':' + this.configuration.password)
      );
    }

    if (this.configuration.accessToken) {
      const accessToken =
        typeof this.configuration.accessToken === 'function'
          ? this.configuration.accessToken()
          : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    const httpHeaderAccepts: string[] = ['application/json;charset=utf-8'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    return this.httpClient.request<any>(
      'get',
      `${this.baseConfigPath}/troubleTicket/dashboard/stats`,
      {
        params: queryParameters,
        withCredentials: this.configuration.withCredentials,
        headers: headers,
      }
    );
  }

  /**
   * Retrieve a Clearing Ticket Possible target states by ID
   * This operation retrieves a Clearing Ticket Possible target states
   * @param id Identifier of the ClearingTicket
   * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
   * @param reportProgress flag to report request and response progress.
   */
  public listPossibleTargetStates(
    id: string,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<Array<TargetStateDto>>;
  public listPossibleTargetStates(
    id: string,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<Array<TargetStateDto>>>;
  public listPossibleTargetStates(
    id: string,
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<Array<TargetStateDto>>>;
  public listPossibleTargetStates(
    id: string,
    observe: any = 'body',
    reportProgress = false
  ): Observable<any> {
    if (id === null || id === undefined) {
      throw new Error(
        'Required parameter id was null or undefined when calling listPossibleTargetStates.'
      );
    }

    let headers = this.defaultHeaders;

    // authentication (basicAuth) required
    if (this.configuration.username || this.configuration.password) {
      headers = headers.set(
        'Authorization',
        'Basic ' +
          btoa(this.configuration.username + ':' + this.configuration.password)
      );
    }

    // authentication (bearer) required
    if (this.configuration.accessToken) {
      const accessToken =
        typeof this.configuration.accessToken === 'function'
          ? this.configuration.accessToken()
          : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }
    // to determine the Accept header
    const httpHeaderAccepts: string[] = ['application/json;charset=utf-8'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = [];
    return this.httpClient.request<Array<TargetStateDto>>(
      'get',
      `${this.baseConfigPath}/troubleTicket/${encodeURIComponent(
        String(id)
      )}/available-states`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress,
      }
    );
  }

  /**
   * Retrieve a Clearing Ticket Clearing Data  by Scenario ID
   * This operation retrieves a  Clearing Ticket Clearing Data
   * @param id Identifier of the ClearingTicket Scenario
   * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
   * @param reportProgress flag to report request and response progress.
   */
  public listClearingDataByScenarioId(
    id: string,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<Array<ClearingDataAttribute>>;
  public listClearingDataByScenarioId(
    id: string,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<Array<ClearingDataAttribute>>>;
  public listClearingDataByScenarioId(
    id: string,
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<Array<ClearingDataAttribute>>>;
  public listClearingDataByScenarioId(
    id: string,
    observe: any = 'body',
    reportProgress = false
  ): Observable<any> {
    if (id === null || id === undefined) {
      throw new Error(
        'Required parameter id was null or undefined when calling listClearingDataByScenarioId.'
      );
    }

    let headers = this.defaultHeaders;

    // authentication (basicAuth) required
    if (this.configuration.username || this.configuration.password) {
      headers = headers.set(
        'Authorization',
        'Basic ' +
          btoa(this.configuration.username + ':' + this.configuration.password)
      );
    }

    // authentication (bearer) required
    if (this.configuration.accessToken) {
      const accessToken =
        typeof this.configuration.accessToken === 'function'
          ? this.configuration.accessToken()
          : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }
    // to determine the Accept header
    const httpHeaderAccepts: string[] = ['application/json;charset=utf-8'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = [];
    return this.httpClient.request<Array<ClearingDataAttribute>>(
      'get',
      `${this.baseConfigPath}/troubleTicket/scenarios/${encodeURIComponent(
        String(id)
      )}/clearingData`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress,
      }
    );
  }

  /**
   * Retrieve a Clearing Ticket Clearing Data Form Array  by Scenario ID for specific ClearingDataAttribute
   * This operation retrieves a  Clearing Ticket Clearing Data for specific attribute
   * @param scenarioId Identifier of the ClearingTicket Scenario
   * @param attributeName Identifier of the Specific ClearingDataAttribute
   * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
   * @param reportProgress flag to report request and response progress.
   */
  public listClearingDataByScenarioIdAndAttributeName(
    scenarioId: string,
    attributeName: string,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<ClearingDataFormArrayDto>;
  public listClearingDataByScenarioIdAndAttributeName(
    scenarioId: string,
    attributeName: string,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<ClearingDataFormArrayDto>>;
  public listClearingDataByScenarioIdAndAttributeName(
    scenarioId: string,
    attributeName: string,
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<ClearingDataFormArrayDto>>;
  public listClearingDataByScenarioIdAndAttributeName(
    scenarioId: string,
    attributeName: string,
    observe: any = 'body',
    reportProgress = false
  ): Observable<any> {
    if (scenarioId === null || scenarioId === undefined) {
      throw new Error(
        'Required parameter id was null or undefined when calling listClearingDataByScenarioIdAndAttributeName.'
      );
    }

    if (attributeName === null || attributeName === undefined) {
      throw new Error(
        'Required parameter attributeName was null or undefined when calling listClearingDataByScenarioIdAndAttributeName.'
      );
    }

    let headers = this.defaultHeaders;

    // authentication (basicAuth) required
    if (this.configuration.username || this.configuration.password) {
      headers = headers.set(
        'Authorization',
        'Basic ' +
          btoa(this.configuration.username + ':' + this.configuration.password)
      );
    }

    // authentication (bearer) required
    if (this.configuration.accessToken) {
      const accessToken =
        typeof this.configuration.accessToken === 'function'
          ? this.configuration.accessToken()
          : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }
    // to determine the Accept header
    const httpHeaderAccepts: string[] = ['application/json;charset=utf-8'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = [];
    return this.httpClient.request<Array<ClearingDataFormArrayDto>>(
      'get',
      `${this.baseConfigPath}/troubleTicket/scenarios/${encodeURIComponent(
        String(scenarioId)
      )}/${encodeURIComponent(String(attributeName))}/clearingData`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress,
      }
    );
  }

  public listPlatformPartnerConfiguration() {
    return this.httpClient.request<Array<PlatformPartner>>(
      'get',
      `${this.baseConfigPath}/platformPartner`
    );
  }

  public retrievePlatformPartnerConfiguration(id: string) {
    return this.httpClient.request<PlatformPartner>(
      'get',
      `${this.baseConfigPath}/platformPartner/${id}`
    );
  }

  public createPlatformPartnerConfiguration(
    platformPartnerCreate: PlatformPartner
  ) {
    return this.httpClient.request<PlatformPartner>(
      'post',
      `${this.baseConfigPath}/platformPartner`,
      {
        body: platformPartnerCreate,
      }
    );
  }

  public updatePlatformPartnerConfiguration(
    id: number,
    platformPartnerUpdate: PlatformPartner
  ) {
    return this.httpClient.request<PlatformPartner>(
      'patch',
      `${this.baseConfigPath}/platformPartner/${id}`,
      {
        body: platformPartnerUpdate,
      }
    );
  }

  public listMyNotifications() {
    return this.httpClient.request<Notification[]>(
      'get',
      `${this.baseConfigPath}/troubleTicket/notifications`
    );
  }

  public listUnreadTicketIds() {
    return this.httpClient.request<string[]>(
      'post',
      `${this.baseConfigPath}/troubleTicket/unread`
    );
  }

  public listTicketIdsWithSlaResolutionTime(tickets: ClearingTicketBase[]) {
    return this.httpClient.request<TicketSlaResolutionTimeStatus[]>(
      'post',
      `${this.baseConfigPath}/troubleTicket/sla/status`,
      {
        body: tickets,
      }
    );
  }

  public readMyNotificationsByTicketId(ticketId: string) {
    return this.httpClient.request(
      'post',
      `${this.baseConfigPath}/troubleTicket/notifications/${ticketId}`
    );
  }

  public markAllUserSentNotificationsAsRead() {
    return this.httpClient.request(
      'post',
      `${this.baseConfigPath}/troubleTicket/notifications/sent/read`
    );
  }

  public markAllUserNotificationsAsRead(
    ticketCategory: TicketCategoryEnum | null
  ) {
    return this.httpClient.request(
      'post',
      `${this.baseConfigPath}/troubleTicket/notifications/${ticketCategory}/read`
    );
  }

  public markBulkUserNotificationsAsRead(clearingTicketIds: string[]) {
    return this.httpClient.request(
      'post',
      `${this.baseConfigPath}/troubleTicket/notifications/bulk/read`,
      {
        body: clearingTicketIds,
      }
    );
  }

  public createRoutingConfig(routingConfigCreate: RoutingConfig) {
    return this.httpClient.request<RoutingConfig>(
      'post',
      `${this.baseConfigPath}/routing-config`,
      {
        body: routingConfigCreate,
      }
    );
  }


  public listLightTicketActionsEligibleUsers(clearingTicketId: string) {
    return this.httpClient.get<UserEligibilityDto>(
      `${this.baseConfigPath}/troubleTicket/${clearingTicketId}/userEligibility`
    );
  }

  public listRoutingConfig(
    type: RoutingConfigTypeEnum,
    page?: number,
    pageSize?: number
  ): Observable<HttpResponse<RoutingConfig[]>> {
    return this.httpClient.request<RoutingConfig[]>(
      'get',
      `${this.baseConfigPath}/routing-config`,
      {
        params: {
          page: page || 0,
          size: pageSize || 10,
          type: type,
        },
        observe: 'response',
      }
    );
  }

  public retrieveRoutingConfig(id: string) {
    return this.httpClient.request<RoutingConfig>(
      'get',
      `${this.baseConfigPath}/routing-config/${id}`
    );
  }

  public retrieveRoutingConfigByScenario(scenarioId: string) {
    return this.httpClient.request<RoutingConfig[]>(
      'get',
      `${this.baseConfigPath}/routing-config/scenario/${scenarioId}`
    );
  }

  public updateRoutingConfig(id: number, routingConfigUpdate: RoutingConfig) {
    return this.httpClient.request<RoutingConfig>(
      'put',
      `${this.baseConfigPath}/routing-config/${id}`,
      {
        body: routingConfigUpdate,
      }
    );
  }

  public deleteRoutingConfig(id: number) {
    return this.httpClient.request(
      'delete',
      `${this.baseConfigPath}/routing-config/${id}`
    );
  }

  public createSlaConfig(slaConfigCreate: SlaPartnerResolutionTime) {
    return this.httpClient.request<SlaPartnerResolutionTime>(
      'post',
      `${this.baseConfigPath}/sla`,
      {
        body: slaConfigCreate,
      }
    );
  }

  public createClearingTicketTemplate(
    clearingTicketTemplate: ClearingTicketTemplate
  ) {
    return this.httpClient.request<ClearingTicketTemplate>(
      'post',
      `${this.baseConfigPath}/troubleTicket/template`,
      {
        body: clearingTicketTemplate,
      }
    );
  }

  public listClearingTicketTemplate() {
    return this.httpClient.request<Array<ClearingTicketTemplate>>(
      'get',
      `${this.baseConfigPath}/troubleTicket/template`
    );
  }

  public deleteClearingTicketTemplate(id: number) {
    return this.httpClient.request<void>(
      'delete',
      `${this.baseConfigPath}/troubleTicket/template/${id}`
    );
  }

  public listSlaConfig(
    page?: number,
    pageSize?: number
  ): Observable<HttpResponse<SlaPartnerResolutionTime[]>> {
    return this.httpClient.request<SlaPartnerResolutionTime[]>(
      'get',
      `${this.baseConfigPath}/sla`,
      {
        params: {
          page: page || 0,
          size: pageSize || 10,
        },
        observe: 'response',
      }
    );
  }

  public updateSlaConfig(
    id: number,
    slaConfigUpdate: SlaPartnerResolutionTime
  ) {
    return this.httpClient.request<RoutingConfig>(
      'put',
      `${this.baseConfigPath}/sla/${id}`,
      {
        body: slaConfigUpdate,
      }
    );
  }

  public retrieveSlaConfig(id: string) {
    return this.httpClient.request<SlaPartnerResolutionTime>(
      'get',
      `${this.baseConfigPath}/sla/${id}`
    );
  }

  public deleteSlaConfig(id: number) {
    return this.httpClient.request(
      'delete',
      `${this.baseConfigPath}/sla/${id}`
    );
  }

  public listAccessRoles() {
    return this.httpClient.request<string[]>(
      'get',
      `${this.baseConfigPath}/access/roles`
    );
  }

  public listAccessBaseRoles() {
    return this.httpClient.request<string[]>(
      'get',
      `${this.baseConfigPath}/access/roles/base`
    );
  }

  public listDynamicAccessRoles() {
    return this.httpClient.request<DynamicAccessRole[]>(
      'get',
      `${this.baseConfigPath}/access/roles/dynamic`
    );
  }

  public createDynamicAccessRole(createDynamicAccessRole: CreateDynamicAccessRole) {
    return this.httpClient.request<DynamicAccessRole>(
      'post',
      `${this.baseConfigPath}/access/roles/dynamic`,
      {
        body: createDynamicAccessRole,
      }
    );
  }

  sync(id: string) {
    return this.httpClient.request(
      'patch',
      `${this.baseConfigPath}/troubleTicket/${id}/sync`
    );
  }

  bulkSync(ids: string[]) {
    return this.httpClient.request(
      'patch',
      `${this.baseConfigPath}/troubleTicket/sync`,
      {
        body: ids,
      }
    );
  }

  getSync(ticketId: string) {
    return this.httpClient.request<SyncChangeDto>(
      'get',
      `${this.baseConfigPath}/troubleTicket/${ticketId}/sync`
    );
  }

  syncEmail(id: string) {
    return this.httpClient.request(
      'patch',
      `${this.baseConfigPath}/troubleTicket/${id}/sync-email`
    );
  }

  bulkSyncEmail(ids: string[]) {
    return this.httpClient.request(
      'patch',
      `${this.baseConfigPath}/troubleTicket/sync-email`,
      {
        body: ids,
      }
    );
  }

  getSyncEmail(ticketId: string) {
    return this.httpClient.request<SyncChangeDto>(
      'get',
      `${this.baseConfigPath}/troubleTicket/${ticketId}/sync-email`
    );
  }

  public requestCancellation(
    requestCancellationCreateDto: RequestCancellationCreateDto
  ) {
    return this.httpClient.request<void>(
      'post',
      `${this.baseConfigPath}/troubleTicket/requestCancellation`,
      {
        body: requestCancellationCreateDto,
      }
    );
  }

  public resolveProxyTarget(ticketId: string, targetItuCode: string) {
    return this.httpClient.request<void>(
      'patch',
      `${this.baseConfigPath}/troubleTicket/${ticketId}/proxy/target/${encodeURIComponent(
        String(targetItuCode)
      )}`
    );
  }

  public retrieveClearingTicketExtended(ticketId: string) {
    return this.httpClient.request<ClearingTicketBase>(
      'get',
      `${this.baseConfigPath}/troubleTicket/${ticketId}/extended`
    );
  }

  public isChangeReasonRequired(
    fromStatus: string,
    toStatus: string): Observable<boolean> {
    const headers = this.defaultHeaders;
    const queryParams = new HttpParams()
      .set('fromStatus', fromStatus)
      .set('toStatus', toStatus);

    return this.httpClient.get<boolean>(
      `${this.baseConfigPath}/troubleTicket/statusChangeReasonRequired`,
      {
        headers: headers,
        params: queryParams,
      }
    );
  }

  /**
   * Lists or finds ClearingTicket objects
   * This operation lists ClearingTicket entities
   * @param searchQuery Search query by which to filter results. An empty parameter is ignored.
   * @param externalId Select only clearing tickets with externalId equal &#x27;externalId&#x27;. An empty parameter is ignored.
   * @param ticketType Select only clearing tickets with scenarios &#x3D; &#x27;ticketType&#x27; (allows pattern: e.g. 1.* selects all scenarios 1.0 — 1.9). An empty parameter is ignored.
   * @param status Select only clearing tickets with status equal &#x27;status&#x27;. An empty parameter is ignored.
   * @param originator Select only clearing tickets with originator equal &#x27;originator&#x27;. An empty parameter is ignored.
   * @param processor Select only clearing tickets with processor equal &#x27;processor&#x27;. An empty parameter is ignored.
   * @param severity Select only clearing tickets with severity equal &#x27;severity&#x27;. An empty parameter is ignored.
   * @param requestedResolutionDateFrom Select only clearing tickets with requestedResolutionDate higher &#x27;requestedResolutionDate&#x27;. An empty parameter is ignored.
   * @param requestedResolutionDateTo Select only clearing tickets with requestedResolutionDate less equal &#x27;requestedResolutionDate&#x27;. An empty parameter is ignored.
   * @param creationDateFrom Select only clearing tickets with creation date equal or higher &#x27;creationDateFrom&#x27; An empty parameter is ignored.
   * @param creationDateTo Select only clearing tickets with creation date less equal &#x27;creationDateTo&#x27; An empty parameter is ignored.
   * @param lastUpdateFrom Select only clearing tickets with last update equal or higher &#x27;lastUpdateFrom&#x27;. An empty parameter is ignored.
   * @param lastUpdateTo Select only clearing tickets with last update less equal &#x27;lastUpdateTo&#x27;. An empty parameter is ignored.
   * @param offset Requested index for start of selected clearing tickets to be provided in response. Note: Sort order is always by creation date.
   * @param limit Requested number of selected clearing tickets to be provided in response. Note: Sort order is always by creation date.
   * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
   * @param reportProgress flag to report request and response progress.
   */
  public listClearingTicket(
    searchQuery?: string,
    externalId?: string,
    ticketType?: string,
    status?: StatusType,
    originator?: string,
    processor?: string,
    severity?: SeverityType,
    requestedResolutionDateFrom?: string,
    requestedResolutionDateTo?: string,
    creationDateFrom?: string,
    creationDateTo?: string,
    lastUpdateFrom?: Date,
    lastUpdateTo?: Date,
    unread?: boolean,
    slaWarning?: boolean,
    slaExceeded?: boolean,
    notSynced?: boolean,
    proxyWaitingTargetResolution?: boolean,
    myIssues?:boolean,
    myWatchingIssues?:boolean,
    inbox?:boolean,
    outbox?:boolean,
    allTickets?: boolean | undefined,
    finalizedOrClosedOnly?: boolean | undefined,
    offset?: number,
    limit?: number,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<Array<ClearingTicketBase>>;
  public listClearingTicket(
    searchQuery?: string,
    externalId?: string,
    ticketType?: string,
    status?: StatusType,
    originator?: string,
    processor?: string,
    severity?: SeverityType,
    requestedResolutionDateFrom?: string,
    requestedResolutionDateTo?: string,
    creationDateFrom?: string,
    creationDateTo?: string,
    lastUpdateFrom?: Date,
    lastUpdateTo?: Date,
    unread?: boolean,
    slaWarning?: boolean,
    slaExceeded?: boolean,
    notSynced?: boolean,
    proxyWaitingTargetResolution?: boolean,
    myIssues?:boolean,
    myWatchingIssues?:boolean,
    inbox?:boolean,
    outbox?:boolean,
    allTickets?: boolean | undefined,
    finalizedOrClosedOnly?: boolean | undefined,
    offset?: number,
    limit?: number,
    observe?: 'response',
    reportProgress?: boolean
  ): Observable<HttpResponse<Array<ClearingTicketBase>>>;
  public listClearingTicket(
    searchQuery?: string,
    externalId?: string,
    ticketType?: string,
    status?: StatusType,
    originator?: string,
    processor?: string,
    severity?: SeverityType,
    requestedResolutionDateFrom?: string,
    requestedResolutionDateTo?: string,
    creationDateFrom?: string,
    creationDateTo?: string,
    lastUpdateFrom?: Date,
    lastUpdateTo?: Date,
    unread?: boolean,
    slaWarning?: boolean,
    slaExceeded?: boolean,
    notSynced?: boolean,
    proxyWaitingTargetResolution?: boolean,
    myIssues?:boolean,
    myWatchingIssues?:boolean,
    inbox?:boolean,
    outbox?:boolean,
    allTickets?: boolean | undefined,
    finalizedOrClosedOnly?: boolean | undefined,
    offset?: number,
    limit?: number,
    observe?: 'events',
    reportProgress?: boolean
  ): Observable<HttpEvent<Array<ClearingTicketBase>>>;
  public listClearingTicket(
    searchQuery?: string,
    externalId?: string,
    ticketType?: string,
    status?: StatusType,
    originator?: string,
    processor?: string,
    severity?: SeverityType,
    requestedResolutionDateFrom?: string,
    requestedResolutionDateTo?: string,
    creationDateFrom?: string,
    creationDateTo?: string,
    lastUpdateFrom?: Date,
    lastUpdateTo?: Date,
    unread?: boolean,
    slaWarning?: boolean,
    slaExceeded?: boolean,
    notSynced?: boolean,
    proxyWaitingTargetResolution?: boolean,
    myIssues?:boolean,
    myWatchingIssues?:boolean,
    inbox?:boolean,
    outbox?:boolean,
    allTickets?: boolean | undefined,
    finalizedOrClosedOnly?: boolean | undefined,
    offset?: number,
    limit?: number,
    observe: any = 'body',
    reportProgress: boolean = false
  ): Observable<any> {
    //TODO: this is hotfix and should be changed to for example 'currentStatus NOT resolved' but currently this is not supported on backed
    let allSearchQuery = '(currentStatus:acknowledged OR currentStatus:pending OR currentStatus:held OR currentStatus:inProgress OR currentStatus:cancelled OR currentStatus:resolved OR currentStatus:final OR currentStatus:closed)'
    let allExceptFinalOrClosedSearchQuery = '(currentStatus:acknowledged OR currentStatus:pending OR currentStatus:held OR currentStatus:inProgress OR currentStatus:cancelled OR currentStatus:resolved)'
    let finalOrClosedOnlySearchQuery= '(currentStatus:final OR currentStatus:closed)'

    let queryParameters = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });

    if (status !== undefined && status !== null) {
      if(status=== StatusType.Closed || status=== StatusType.Final) allExceptFinalOrClosedSearchQuery = ''
      queryParameters = queryParameters.set('status', <any>status);
    }

    if (searchQuery !== undefined && searchQuery !== null && searchQuery !== '') {
        if(finalizedOrClosedOnly){
          queryParameters = queryParameters.set('searchQuery', <any>searchQuery + ' AND ' + finalOrClosedOnlySearchQuery);
        } else if (allTickets){
          queryParameters = queryParameters.set('searchQuery', <any>searchQuery + ' AND ' + allSearchQuery);
        }
        else {
          queryParameters = queryParameters.set('searchQuery', <any>searchQuery + ' AND ' + allExceptFinalOrClosedSearchQuery);
        }
    }
    else {
      if(finalizedOrClosedOnly){
        queryParameters = queryParameters.set('searchQuery', <any>finalOrClosedOnlySearchQuery);
      } else if (allTickets){
        queryParameters = queryParameters.set('searchQuery', <any>allSearchQuery);
      }
      else {
        queryParameters = queryParameters.set('searchQuery', <any>allExceptFinalOrClosedSearchQuery);
      }
    }

    if (externalId !== undefined && externalId !== null) {
      queryParameters = queryParameters.set('externalId', <any>externalId);
    }
    if (ticketType !== undefined && ticketType !== null) {
      queryParameters = queryParameters.set('ticketType', <any>ticketType);
    }
    if (originator !== undefined && originator !== null) {
      queryParameters = queryParameters.set('originator', <any>originator);
    }
    if (processor !== undefined && processor !== null) {
      queryParameters = queryParameters.set('processor', <any>processor);
    }
    if (severity !== undefined && severity !== null) {
      queryParameters = queryParameters.set('severity', <any>severity);
    }
    if (
      requestedResolutionDateFrom !== undefined &&
      requestedResolutionDateFrom !== null
    ) {
      queryParameters = queryParameters.set(
        'requestedResolutionDateFrom',
        <any>requestedResolutionDateFrom
      );
    }
    if (
      requestedResolutionDateTo !== undefined &&
      requestedResolutionDateTo !== null
    ) {
      queryParameters = queryParameters.set(
        'requestedResolutionDateTo',
        <any>requestedResolutionDateTo
      );
    }
    if (creationDateFrom !== undefined && creationDateFrom !== null) {
      queryParameters = queryParameters.set(
        'creationDateFrom',
        <any>creationDateFrom
      );
    }
    if (creationDateTo !== undefined && creationDateTo !== null) {
      queryParameters = queryParameters.set(
        'creationDateTo',
        <any>creationDateTo
      );
    }
    if (lastUpdateFrom !== undefined && lastUpdateFrom !== null) {
      queryParameters = queryParameters.set(
        'lastUpdateFrom',
        <any>lastUpdateFrom.toISOString()
      );
    }
    if (lastUpdateTo !== undefined && lastUpdateTo !== null) {
      queryParameters = queryParameters.set(
        'lastUpdateTo',
        lastUpdateTo.toISOString()
      );
    }
    if (offset !== undefined && offset !== null) {
      queryParameters = queryParameters.set('offset', <any>offset);
    }
    if (limit !== undefined && limit !== null) {
      queryParameters = queryParameters.set('limit', <any>limit);
    }

    let headers = this.defaultHeaders;

    // authentication (basicAuth) required
    if (this.configuration.username || this.configuration.password) {
      headers = headers.set(
        'Authorization',
        'Basic ' +
        btoa(this.configuration.username + ':' + this.configuration.password)
      );
    }

    // authentication (bearer) required
    if (this.configuration.accessToken) {
      const accessToken =
        typeof this.configuration.accessToken === 'function'
          ? this.configuration.accessToken()
          : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }
    // to determine the Accept header
    let httpHeaderAccepts: string[] = ['application/json;charset=utf-8'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    const customFilters = [];
    if (unread) {
      customFilters.push(CustomFiltersEnum.UNREAD);
    }
    if (slaWarning) {
      customFilters.push(CustomFiltersEnum.SLA_WARNING);
    }
    if (outbox) {
      customFilters.push(CustomFiltersEnum.OUTBOX);
    }
    if (inbox) {
      customFilters.push(CustomFiltersEnum.INBOX);
    }
    if (slaExceeded) {
      customFilters.push(CustomFiltersEnum.SLA_EXCEEDED);
    }
    if (notSynced) {
      customFilters.push(CustomFiltersEnum.NOT_SYNCED);
    }
    if (proxyWaitingTargetResolution) {
      customFilters.push(CustomFiltersEnum.PROXY_WAITING_TARGET_RESOLUTION);
    }
    if(myIssues){
      customFilters.push(CustomFiltersEnum.MY_ISSUES);
    }
    if(myWatchingIssues){
      customFilters.push(CustomFiltersEnum.MY_WATCHING_ISSUES);
    }
    headers = headers.set('Custom-Filter', customFilters);

    // to determine the Content-Type header
    const consumes: string[] = [];
    return this.httpClient.request<Array<ClearingTicketBase>>(
      'get',
      `${this.baseConfigPath}/troubleTicket/search`,
      {
        params: queryParameters,
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress,
      }
    );
  }

  public listPlatformPartners(): Observable<string[]> {
    const headers = this.defaultHeaders;
    const queryParams = new HttpParams()
      .set('platform', "DEU.BTCEX");

    return this.httpClient.get<string[]>(
      `${this.baseConfigPath}/platformPartners`,
      {
        headers: headers,
        params: queryParams
      }
    );
  }

  public getOwnerCode(ticketId: string) {
    const headers = this.defaultHeaders;
    return this.httpClient.get<string>(
      `${this.baseConfigPath}/troubleTicket/${ticketId}/owner`,
      {
        headers: headers,
        responseType: 'text' as 'json'
      }
    );
  }

  public getCurrentOwnerScope(): Observable<OwnerScopeDto> {
    const headers = this.defaultHeaders;
    return this.httpClient.get<OwnerScopeDto>(
      `${this.baseConfigPath}/troubleTicket/owner/scope`,
      {
        headers: headers,
      }
    );
  }
}
